import { useEffect, useState } from "react";
import { DatePicker, Spin } from "antd";
import { get } from "aws-amplify/api";
import "chart.js/auto";
import {
  NEW_REST_API,
  NEW_REST_API__V2__SF_ANALYTICS_PATH,
} from "../../consts";
import { SessionsPerDay } from "./Analytics/SessionsPerDay";
import { SessionsPerCountry } from "./Analytics/SessionsPerCountry";
import { Funnel } from "./Analytics/Funnel";
import "./Analytics/Analytics.scss";
import { UsersPerDay } from "./Analytics/UsersPerDay";
import { TopCtas } from "./Analytics/TopCtas";
import { WatchTime } from "./Analytics/WatchTime";
import { SessionsPerDeviceType } from "./Analytics/SessionsPerDeviceType";
import { SessionsPerOs } from "./Analytics/SessionsPerOs";
import { theme } from "../../theme";
import { DynamicThumbnail } from "./Analytics/DynamicThumbnail";
import { LoadingTime } from "./Analytics/LoadingTime";
import { ScenePaths } from "./Analytics/ScenePaths";
import dayjs from "dayjs";

// Shape returned by the /sf-analytics endpoint (mirrors
// SnowflakeAnalyticsResponse on the backend). Kept here as a frontend-local
// type so the View layer doesn't import server types.
export type SessionsPerDayPoint = {
  date: string;
  timestamp: number;
  count: number;
};

export type UsersPerDayPoint = {
  date: string;
  timestamp: number;
  uniqueUsers: number;
  totalSessions: number;
};

export type CountryPoint = { country: string; count: number };
export type OsPoint = { os: string; count: number };
export type DevicePoint = { device: string; count: number };

export type WatchTimeFrame = { frameNumber: number; viewerCount: number };
export type WatchTimeScene = {
  sceneId: string;
  totalViews: number;
  frames: Array<WatchTimeFrame>;
};
export type WatchTimeOverallPoint = {
  timeSeconds: number;
  viewersRemaining: number;
};

export type CtaClick = {
  ctaName: string;
  clickCount: number;
  uniqueSessions: number;
};

export type InteractivesScene = {
  sceneId: string;
  totalClicks: number;
  uniqueSessions: number;
  ctas: Array<CtaClick>;
};

export type SceneProgressionPoint = {
  sceneId: string;
  eventCount: number;
  uniqueSessions: number;
};

export type DynamicThumbnailPoint = {
  date: string;
  timestamp: number;
  count: number;
};

export type ScenePathPoint = { path: string; count: number };

export type SfAnalyticsResponse = {
  sessions: {
    perDay: Array<SessionsPerDayPoint>;
    perCountry: Array<CountryPoint>;
    perOs: Array<OsPoint>;
    perDevice: Array<DevicePoint>;
    total: number;
  };
  users: {
    perDay: Array<UsersPerDayPoint>;
    totalUnique: number;
  };
  loading: {
    medianTimeMs: number;
  };
  watchTime: {
    perScene: Array<WatchTimeScene>;
    overall: Array<WatchTimeOverallPoint>;
  } | null;
  playerEvents: {
    sdkLoaded: number;
    videoDefLoaded: number;
    ready: number;
    firstPlay: number;
    play: number;
    interactiveElements: number;
    playerInteractions: number;
    errors: number;
  } | null;
  scenePaths: Array<ScenePathPoint> | null;
  ctas: {
    totalSessionsWithClicks: number;
    clicks: Array<CtaClick>;
  } | null;
  interactives: {
    perScene: Array<InteractivesScene>;
  } | null;
  dynamicThumbnails: {
    count: number;
    perDay: Array<DynamicThumbnailPoint>;
  } | null;
  scenesProgression: Array<SceneProgressionPoint> | null;
};

const DEFAULT_RANGE = 30 * 24 * 60 * 60 * 1000;
const colors = [
  theme.primary,
  "#ff5683",
  "#ff82b1",
  "#ffaad6",
  "#ffcff0",
  "#03c2dd",
  "#a3d0fd",
  "#ece3ff",
];

export function AnalyticsNew({ projectId }: { projectId: string }) {
  const [fromDate, setFromDate] = useState(Date.now() - DEFAULT_RANGE);
  const [showScenePath, setShowScenePath] = useState(false);
  const [toDate, setToDate] = useState(Date.now());
  const [data, setData] = useState<SfAnalyticsResponse | null>(null);

  useEffect(() => {
    (async () => {
      const data = await get({
        apiName: NEW_REST_API,
        path: NEW_REST_API__V2__SF_ANALYTICS_PATH(projectId),
        options: {
          queryParams: {
            from: fromDate.toString(),
            to: toDate.toString(),
          },
        },
      }).response;
      setData((await data.body.json()) as SfAnalyticsResponse);
    })();
  }, [fromDate, toDate, projectId]);

  return (
    <div className="analytics">
      <div
        className="title"
        style={{ position: "absolute", right: "3rem", top: "20px" }}
      >
        <DatePicker.RangePicker
          className="date-picker"
          defaultPickerValue={[dayjs(fromDate), dayjs(toDate)]}
          value={[dayjs(fromDate), dayjs(toDate)]}
          onChange={(values) => {
            if (!values) return;
            const to = (values[1] && values[1].valueOf()) || Date.now();
            const from =
              (values[0] && values[0].valueOf()) || to - DEFAULT_RANGE;
            setToDate(to);
            setFromDate(from);
          }}
          onCalendarChange={(values, dateStrings, info) => {
            if (!values) return;

            // Honor the exact picked instant — the picker has showTime, so this
            // must match onChange. It previously forced startOf/endOf("day"),
            // which collapsed to the same window only while the backend
            // truncated to the calendar day; now that it filters on the precise
            // timestamp (BIO-3485), the two handlers would otherwise select
            // different windows for the same selection.
            if (info.range === "start" && values[0]) {
              setFromDate(values[0].valueOf());
            } else if (info.range === "end" && values[1]) {
              setToDate(values[1].valueOf());
            }
          }}
          showTime={{ format: "HH:mm" }}
          showNow={true}
          format="MMM DD, YYYY HH:mm"
        />
      </div>

      {data ? (
        <div className="views-container">
          <Funnel
            data={{
              ready: data.playerEvents?.ready,
              firstPlay: data.playerEvents?.firstPlay,
              scenesProgression: data.scenesProgression ?? undefined,
              interactives: data.interactives?.perScene,
            }}
          />
          <TopCtas data={data.ctas} firstPlay={data.playerEvents?.firstPlay} />
          {data.dynamicThumbnails?.count ? (
            <DynamicThumbnail
              count={data.dynamicThumbnails.count}
              perDay={data.dynamicThumbnails.perDay}
            />
          ) : (
            ""
          )}
          <SessionsPerDay data={data.sessions.perDay} />
          <UsersPerDay
            data={data.users.perDay}
            uniqueUsers={data.users.totalUnique}
          />
          <WatchTime
            watchTimesPerScene={data.watchTime?.perScene}
            watchTimesOverall={data.watchTime?.overall}
          />
          <LoadingTime medianTime={data.loading.medianTimeMs || 0} />
          <div className="bottom-row">
            <SessionsPerCountry data={data.sessions.perCountry} />
            <div className="per-device">
              <SessionsPerDeviceType
                data={data.sessions.perDevice}
                colors={colors}
              />

              <SessionsPerOs data={data.sessions.perOs} colors={colors} />
            </div>
          </div>

          {showScenePath ? <ScenePaths data={data.scenePaths} /> : null}
          <div
            className="easterEgg"
            style={{
              display: "none",
              width: "10px",
              height: "10px",
              backgroundColor: "red",
            }}
            onClick={() => {
              setShowScenePath(true);
            }}
          ></div>
        </div>
      ) : (
        <Spin style={{ margin: "10px" }} />
      )}
    </div>
  );
}
